import React from 'react';
import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';

import Logo from '../../assets/logo.png';
import TruckImage from '../../assets/truck.png';

import DateUtils from 'helpers/dateUtils';
import NumberUtils from 'helpers/numberUtils';

const dateUtils = new DateUtils();
const numberUtils = new NumberUtils();

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    paddingTop: 20,
    backgroundColor: 'white',
  },
  header: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  logo: {
    width: 220,
  },
  headerInfo: {
    width: '400px',
    textAlign: 'center',
    color: '#000',
    fontSize: 10,
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'row',
  },
  headerDataLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: '#000',
  },
  title: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: 'center',
  },
  companyName: {
    fontSize: 16,
    color: 'red',
    textAlign: 'center',
    marginBottom: 20,
  },
  truckImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  truckImage: {
    width: '350px',
    height: '350px',
    objectFit: 'cover',
    marginBottom: 20,
  },
  pricingTitle: {
    fontSize: 16,
    color: 'red',
    textAlign: 'center',
    marginBottom: 15,
  },
  pricingContainer: {
    display: 'flex',
  },
  pricingGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  pricingItem: {
    width: '48%',
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: 10,
  },
  boldText: {
    fontFamily: 'Arial',
    fontWeight: 'bold',
  },
  value: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  footnote: {
    fontSize: 8,
    marginTop: 20,
    fontStyle: 'italic',
    textAlign: 'center',
  },
});

// Create Document Component
const PricingInformation = ({ data }) => {
  return (
    <>
      <View style={styles.header}>
        <Image style={styles.logo} src={Logo} />
        <View style={styles.headerInfo}>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              marginRight: 2,
            }}
          >
            <Text style={styles.boldText}>Carrier</Text>
            <Text style={styles.boldText}>Submission #</Text>
            <Text style={styles.boldText}>Company Name</Text>
            <Text style={styles.boldText}>Policy Term</Text>
          </View>
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginLeft: 2,
            }}
          >
            <Text>Cover Whale</Text>
            <Text>{data?.submission_number ?? ''}</Text>
            <Text>
              {data?.quote.insuredInformation.legalName ?? ''}
            </Text>
            <Text>
              {' '}
              {data?.effective_date &&
                dateUtils.formatIsoToCommomDate(
                  data?.effective_date
                )}{' '}
              -{' '}
              {data?.effective_date &&
                dateUtils.formatIsoToCommomDate(
                  dateUtils.addYears(data?.effective_date, 1)
                )}
            </Text>
          </View>
        </View>
      </View>

      <Text style={styles.title}>Commercial Trucking data for:</Text>
      <Text style={styles.companyName}>
        {data?.quote.insuredInformation.legalName ?? ''}
      </Text>

      <View style={styles.truckImageContainer}>
        <Image style={styles.truckImage} src={TruckImage} />
      </View>

      <Text style={styles.pricingTitle}>Pricing Information</Text>

      <View style={styles.pricingContainer}>
        <View style={styles.pricingGrid}>
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              Submission Cost
            </Text>
            <Text style={styles.value}>
              {numberUtils.formatToCurrency(
                data?.financialDetails?.submissionCost
              )}
            </Text>
          </View>
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              Down Payment
            </Text>
            <Text style={styles.value}>
              {numberUtils.formatToCurrency(
                data?.financialDetails?.downPayment +
                  data?.financialDetails?.fee
              )}
            </Text>
          </View>
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              Service Fee
            </Text>
            <Text style={styles.value}>
              {numberUtils.formatToCurrency(
                data?.financialDetails?.fee
              )}
            </Text>
          </View>
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              Monthly Payment
            </Text>
            <Text style={styles.value}>
              {numberUtils.formatToCurrency(
                data?.financialDetails?.paymentAmount
              )}
            </Text>
          </View>
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              Total Cost
            </Text>
            <Text style={[styles.value, styles.boldText]}>
              {numberUtils.formatToCurrency(
                data?.financialDetails?.totalCost
              )}
            </Text>
          </View>
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              # of Payments
            </Text>
            <Text style={styles.value}>
              {data?.financialDetails?.numberOfPayments}
            </Text>
          </View>
          <View style={styles.pricingItem} />
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              Effective APR
            </Text>
            <Text style={styles.value}>
              {`${data?.financialDetails?.effectiveAPR}%`}
            </Text>
          </View>
          <View style={styles.pricingItem} />
          <View style={styles.pricingItem}>
            <Text style={[styles.label, styles.boldText]}>
              Finance Company
            </Text>
            <Text style={styles.value}>
              {data?.financialDetails?.premiumFinanceProvider?.toUpperCase() ??
                ''}
            </Text>
          </View>
        </View>
      </View>

      <Text style={styles.footnote}>
        *All Service Fees are fully earned and are collected as part
        of the down payment. In some circumstances, Cover Badger is
        unable to add the service fee to the PFA. In these cases, it
        will be the responsibility of the retail agent to collect this
        fee directly from the insured at the time of binding the
        submission.
      </Text>
    </>
  );
};

export default PricingInformation;
